<template>
  <v-card class="pa-4 segmentation-map">
    <mf-loading
      v-if="isLoadingMapWaitingActions || loading"
      :loader="['evaluate-segmentation', 'clients-coordinates', 'status-count']"
      class="segmentation-map-container"
      loader-type="content"
      :boolean-loader="!isLoadingMapWaitingActions && loading"
    />
    <div id="map" ref="map">
      <mgl-map ref="mapbox" :access-token="accessToken" :center="getCenter" :map-style="mapStyle" :zoom="8">
        <v-card class="ma-3 map-info-card">
          <div class="d-flex justify-space-between align-center mb-2 pa-3 pb-0">
            <span class="texto-corrido">Clientes no mapa</span>
            <v-tooltip top max-width="300px">
              <template #activator="{ on }">
                <span v-on="on">
                  <mf-icon small color="cinza-l40">info</mf-icon>
                </span>
              </template>
              <span
                >O número de clientes exibidos no mapa pode ser diferente do tamanho da segmentação por falta de informação demográfica desses clientes.</span
              >
            </v-tooltip>
          </div>
          <div class="d-flex justify-space-between align-center px-3 pb-3">
            <span class="big-number">{{ totalShowedClients | localeString }}</span>
            <v-tooltip v-if="showWarningTooltip" top max-width="300px">
              <template #activator="{ on }">
                <span v-on="on">
                  <mf-icon fill small color="vermelho-0">warning</mf-icon>
                </span>
              </template>
              <span
                >O Mapa é limitado a exibição de no máximo 250k de pontos. Ao selecionar um segmento maior que esse limite, alguns pontos aleatórios não serão
                exibidos.</span
              >
            </v-tooltip>
          </div>
          <v-divider />
          <p v-if="totalNotShowedClients" class="subheading mb-0 secondary-text pa-3">+{{ totalNotShowedClients | localeString }} s/ coord</p>
        </v-card>
        <mgl-navigation-control position="bottom-left" />
        <mgl-fullscreen-control position="top-right" />
        <segmentation-map-layer
          v-if="showSegmentationLayer"
          reference="clusterLayer"
          layer-id="cluster"
          :source-id="clusterJsonSource.data.id"
          :source="clusterJsonSource"
          :layer="clusterJsonLayer"
          :click-function="zoomToCluster"
          @clear-clusters="clearClusters"
        />
        <segmentation-map-layer
          v-if="showSegmentationLayer"
          ref="clusterCounterLayer"
          :sourceId="clusterJsonSource.data.id"
          :source="clusterJsonSource"
          layerId="cluster-count"
          :layer="clusterCounterJsonLayer"
          @clear-clusters="clearClusters"
        />
        <segmentation-map-layer
          v-if="showSegmentationLayer"
          ref="unclusteredLayer"
          :sourceId="clusterJsonSource.data.id"
          :source="clusterJsonSource"
          layerId="unclustered"
          :layer="unclusteredLayer"
          @clear-clusters="clearClusters"
        />

        <mgl-marker v-for="unit in getUnitsLayers" :key="unit.name" :coordinates="[unit.longitude, unit.latitude]" color="blue">
          <mgl-popup>
            <div>{{ unit.name }}</div>
          </mgl-popup>
          <img slot="marker" :src="require('@/assets/store_pin.png')" height="50" />
        </mgl-marker>
      </mgl-map>
    </div>
  </v-card>
</template>

<script>
import { MglMap, MglNavigationControl, MglFullscreenControl, MglMarker, MglPopup } from 'vue-mapbox'
import { mapWaitingActions } from 'vue-wait'
import { mapGetters, mapState } from 'vuex'
import isEmpty from 'lodash/isEmpty'
import Mapbox from 'mapbox-gl'
import SegmentationMapLayer from './SegmentationMapLayer.vue'

const MAX_CLIENTS_RESOLVE_NUMBER = 250000
export default {
  name: 'SegmentationMap',
  components: {
    MglMap,
    MglNavigationControl,
    MglFullscreenControl,
    MglMarker,
    MglPopup,
    SegmentationMapLayer,
  },
  data: () => ({
    accessToken: 'pk.eyJ1IjoibWVyY2FmYWNpbCIsImEiOiJjbDM3bWpndnIwdDZuM2NsZHVvNmhteHAxIn0.ISPOboOmgzPnFJNO_CQnsQ', // your access token. Needed if you using Mapbox maps
    isEditingCriterias: false,
    loading: false,
    // Start Cluster
    clusterJsonLayer: {
      id: 'cluster',
      type: 'circle',
      source: 'cluster',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': ['step', ['get', 'point_count'], 'rgba(0, 121, 242, 0.28)', 100, 'rgba(0, 121, 242, 0.28)', 750, 'rgba(0, 121, 242, 0.28)'],
        'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 750, 40],
      },
    },
    clusterCounterJsonLayer: {
      id: 'cluster-count',
      type: 'symbol',
      source: 'cluster',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
    },
    unclusteredLayer: {
      id: 'unclustered',
      type: 'circle',
      source: 'cluster',
      filter: ['!', ['has', 'point_count']],
      paint: {
        'circle-color': 'rgba(0, 99, 198, 0.4)',
        'circle-radius': 4,
        'circle-stroke-width': 1,
        'circle-stroke-color': 'rgba(0, 99, 198, 1)',
      },
    },
    clusterJsonSource: {
      type: 'geojson',
      cluster: true,
      clusterMaxZoom: 14,
      clusterRadius: 50,
      data: {
        id: 'cluster',
        type: 'FeatureCollection',
        features: [],
      },
    },
    // End Cluster
  }),
  computed: {
    ...mapGetters(['segmentationClientsCoordinates', 'segmentationStatusCount']),
    ...mapState({
      unitCoordinates: (state) => state.geo.unitCoordinates,
      segmentationGroups: (state) => state.segmentation.segmentationGroups,
      segmentationSize: (state) => state.segmentation.segmentationSize,
    }),
    getUnitsLayers() {
      if (!this.unitCoordinates) return []
      let units = []
      for (let unit of this.unitCoordinates) {
        units.push({
          latitude: unit['NUM_LAT'],
          longitude: unit['NUM_LNG'],
          name: 'Loja ' + unit['COD_ID_LOJA'],
        })
      }
      return units
    },
    showSegmentationLayer() {
      return this.getFeatures && this.getFeatures.length > 0 && this.clusterJsonSource && this.clusterJsonSource.data
    },
    mapStyle() {
      return 'mapbox://styles/mercafacil/clar1vqke001e14o1pjqgqfu1'
    },
    getCenter() {
      const unit = this.unitCoordinates[0]
      return [unit['NUM_LNG'], unit['NUM_LAT']]
    },
    getFeatures() {
      if (!this.clientsCoordinates.length) return []
      return this.clientsCoordinates.map((item, index) => {
        return {
          type: 'Feature',
          properties: {
            id: `${index}`,
            color: 'rgba(0, 99, 198, 1)',
          },
          geometry: {
            type: 'Point',
            coordinates: [item[1], item[0]],
          },
        }
      })
    },
    isEmptyStatusCount() {
      return isEmpty(this.segmentationStatusCount) || !this.segmentationStatusCount.status
    },
    showWarningTooltip() {
      if (this.isEmptyStatusCount) return ''
      return this.segmentationStatusCount.status.NUM_CLIENTES_TOTAL > MAX_CLIENTS_RESOLVE_NUMBER
    },
    totalShowedClients() {
      if (this.isEmptyStatusCount) return ''
      return this.segmentationStatusCount.status.NUM_CLIENTES_RESOLVIDOS > MAX_CLIENTS_RESOLVE_NUMBER
        ? MAX_CLIENTS_RESOLVE_NUMBER
        : this.segmentationStatusCount.status.NUM_CLIENTES_RESOLVIDOS
    },
    totalNotShowedClients() {
      if (this.isEmptyStatusCount) return ''
      return this.segmentationStatusCount.status.NUM_CLIENTES_NAO_RESOLVIVEIS + this.segmentationStatusCount.status.NUM_CLIENTES_PENDENTES
    },
    clientsCoordinates() {
      if (isEmpty(this.segmentationClientsCoordinates) || !this.segmentationClientsCoordinates.clients_coordinates) return []
      return this.segmentationClientsCoordinates.clients_coordinates
    },
    isLoadingMapWaitingActions() {
      return this.$wait.is(['evaluate-segmentation', 'clients-coordinates', 'status-count'])
    },
    isEvaluatingSegment() {
      return this.$wait.is(['evaluate-segmentation'])
    },
  },
  watch: {
    segmentationSize: {
      deep: true,
      async handler(newVal, oldVal) {
        this.clearClusters()
        this.loading = true
        const loader = document.querySelector('.segmentation-map-container')
        if (loader) loader.classList.remove('d-none')
        await this.getData()
        this.handleLoader()
        this.createMapObject()
      },
    },
  },
  async mounted() {
    await this.getData()
    this.handleLoader()
    this.createMapObject()
    this.mapbox = Mapbox
  },
  beforeDestroy() {
    this.clearClusters()
  },
  methods: {
    ...mapWaitingActions({
      evaluateSegmentation: 'evaluate-segmentation',
      evaluateSegmentationGeoClientsCoordinates: 'clients-coordinates',
      evaluateSegmentationGeoStatusCount: 'status-count',
    }),
    loaded() {
      return this.$refs?.mapbox?.map?.loaded()
    },
    handleLoader() {
      if (!this.loading) return
      const timer = setInterval(() => {
        if (this.loaded()) {
          this.loading = false
          const loader = document.querySelector('.segmentation-map-container')
          if (loader) loader.classList.add('d-none')
          clearInterval(timer)
          return
        }
        this.handleLoader()
      }, 1000)
    },
    createMapObject() {
      this.createClientsCluster()
    },
    createClientsCluster() {
      const features = [...this.getFeatures]
      let tolerance = 0.375
      if (features.length > 100000) tolerance = 2
      if (features.length > 200000) tolerance = 5

      this.clusterJsonSource = {
        type: 'geojson',
        cluster: true,
        clusterMaxZoom: 14,
        clusterRadius: 50,
        tolerance,
        data: {
          id: 'cluster',
          type: 'FeatureCollection',
          features,
        },
      }
    },
    zoomToCluster(cluster) {
      const point = cluster.mapboxEvent.point
      const features = this.$refs.mapbox.map.queryRenderedFeatures(point, { layers: ['cluster'] })
      const clusterId = features[0].properties.cluster_id

      this.$refs.mapbox.map.getSource('cluster').getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return
        this.$refs.mapbox.map.easeTo({
          center: features[0].geometry.coordinates,
          zoom: zoom,
        })
      })
    },
    clearClusters() {
      const ref = this.$refs.mapbox
      if (ref && ref.map && ref.map.getLayer('cluster')) {
        this.$refs.mapbox.map.removeLayer('cluster-count')
        this.$refs.mapbox.map.removeLayer('unclustered')
        this.$refs.mapbox.map.removeLayer('cluster')
        this.$refs.mapbox.map.removeSource('cluster')
        this.clusterJsonSource = null
      }
    },
    clearLayersAndCreateMap() {
      this.clearClusters()
      this.createMapObject()
    },
    async getData() {
      if (this.segmentationSize?.clients_qty === 0) return
      if (!this.segmentationGroups || !this.segmentationGroups?.groups[0]?.criteria?.length) return

      if (isEmpty(this.segmentationClientsCoordinates) || isEmpty(this.segmentationStatusCount)) {
        await Promise.all([
          this.evaluateSegmentationGeoClientsCoordinates({ segment: this.segmentationGroups, maxNumberOfClients: MAX_CLIENTS_RESOLVE_NUMBER }),
          this.evaluateSegmentationGeoStatusCount({ segment: this.segmentationGroups, maxNumberOfClients: MAX_CLIENTS_RESOLVE_NUMBER }),
        ])
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.segmentation-map {
  height: 500px;
  position: relative;
  &-container {
    height: calc(100% - 30px);
    width: calc(100% - 30px);
    position: absolute;
    z-index: 10;
    background: $branco;
  }
  #map {
    overflow: hidden;
    height: 100%;
    z-index: 2;
    position: relative;

    & .map-info-card {
      z-index: 3;
      display: inline-block;
      width: 200px;
    }
  }
}
</style>
